import React from "react";

import { PaymentIcon } from "react-svg-credit-card-payment-icons";
import { Container, CardBody, Popover, Modal } from "reactstrap";
import { isMobile } from "react-device-detect";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { MobileDateRangePickerWrapper } from "@components/Common/MobileDateRangePickerWrapper";
import DateRangePickerWrapper from "@components/Common/DateRangePickerWrapper";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import HeaderComponent from "@components/VerticalLayout/HeaderComponent";
import { StyledPopoverBody } from "@pages/connecs/StyledPopoverBody";
import BreadcrumbOptions from "@pages/Reporting/BreadcrumbOptions";
import { getAdvancedPeriodStringParam } from "@helpers/general";
import { CustomFooter } from "@pages/connecs/CustomFooter";
// import TransactionDetails from "@pages/AdvancedReporting/TransactionDetail";
import NoDataOverlay from "@pages/connecs/NoDataOverlay";
import { changePeriod } from "@store/features/period";
import PageTitle from "@components/Common/PageTitle";
import { RootState } from "@store/index";

import refresh from "../../assets/images/refresh.svg";
import AmountPopoverFilter from "./AmountFilter";
// import RefundModal from "./Refund";

type PaymentRow = {
    id: number;
    date: string;
    Heure: string;
    Amount: string;
    PspReference: string;
    PaymentMethod: string;
    PaymentTerminalId: string;
    CardDetail: string;
};

export function Payments() {
    const { t } = useTranslation();
    const mainColor: string = useSelector(
        (State: RootState): string => State.colorSlice.dominantColor
    );
    const dispatch = useDispatch();
    const [currentPage, setCurrentPage] = React.useState(0);
    const [pageSize, setPageSize] = React.useState(10);
    const [rows, setRows] = React.useState<PaymentRow[]>([]);
    const uuidEntity = localStorage.getItem("Uid_Entitie_User");
    const [btnText, setBtnText] = React.useState<string>(t("Amount"));
    const { periodSlice, advancedDate } = useSelector((state: any) => state);
    const [isRefundModalOpened, setIsRefundModalOpened] =
        React.useState<boolean>(false);
    const [selectedRow, setSelectedRow] = React.useState(null);
    const [showDetails, setShowDetails] = React.useState(false);

    const handleOpenDetails = (row: any) => {
        setSelectedRow(row);
        setShowDetails(!showDetails);
    };

    const [isPopoverOpened, setIsPopoverOpened] = React.useState(false);
    const [filterAmount, setFilterAmount] = React.useState(false);
    const [isModalOpened, setIsModalOpened] = React.useState<boolean>(false);
    const [minAmount, setMinAmount] = React.useState<number | "">("");
    const [maxAmount, setMaxAmount] = React.useState<any | "">("");
    const [selectedDate, setSelectedDate] = React.useState<any | "">("");

    const [filter, setFilter] = React.useState<string>("all");
    const [textIcon, setTextIcon] = React.useState<string>(t("Status"));

    const totalPages = Math.ceil(rows.length / pageSize);
    const handlePageChange = (newPage: number) => {
        setCurrentPage(newPage);
    };

    const handlePageSizeChange = (newPageSize: number) => {
        setPageSize(newPageSize);
        setCurrentPage(0);
    };
    const customHeader = "custom-header";
    type PaymentIconType =
        | "Visa"
        | "Mastercard"
        | "Amex"
        | "Jcb"
        | "Diners"
        | "Discover"
        | "Elo"
        | "Hipercard"
        | "Unionpay"
        | "Maestro"
        | "Mir"
        | "Paypal"
        | "Alipay"
        | "Generic"
        | "Hiper"
        | "Code"
        | "CodeFront";

    const adyenToIconType: Record<string, PaymentIconType> = {
        visa: "Visa",
        mc: "Mastercard",
        mastercard: "Mastercard",
        amex: "Amex",
        jcb: "Jcb",
        diners: "Diners",
        discover: "Discover",
        elo: "Elo",
        hipercard: "Hipercard",
        unionpay: "Unionpay",
        maestro: "Maestro",
        mir: "Mir",
        paypal: "Paypal",
        alipay: "Alipay",
    };
    const localeText = {
        columnMenuSortAsc: t("sortAsc"),
        columnMenuSortDesc: t("sortDesc"),
        columnMenuFilter: t("Filter"),
        columnMenuHideColumn: t("hideColumn"),
        columnMenuShowColumns: t("showColumns"),
        columnMenuUnsort: t("unsort"),
    };

    const columns: GridColDef[] = [
        {
            field: "date",
            headerName: t("Date"),
            headerClassName: customHeader,
            minWidth: 150,
        },
        {
            field: "heure",
            headerName: t("Time"),
            headerClassName: customHeader,
            minWidth: 150,
        },
        {
            field: "PspReference",
            headerName: t("PSP reference"),
            flex: 1,
            renderCell: (params: GridRenderCellParams) => (
                // <Link
                //     onClick={() => handleOpenDetails(params.row)}
                //     style={{ cursor: "pointer", color: "blue" }}
                //     underline="hover"
                // >
                <div>{params.row.PspReference}</div>
                // </Link>
            ),
            headerClassName: customHeader,
            minWidth: 150,
        },
        {
            field: "Amount",
            headerName: t("Amount"),
            flex: 0.75,
            headerClassName: customHeader,
            minWidth: 150,
        },
        {
            field: "PaymentMethod",
            headerName: t("Payment Method"),
            flex: 1,
            headerClassName: customHeader,
            renderCell: (params: GridRenderCellParams) => {
                const method = params.row.PaymentMethod.toLowerCase();
                const iconType: PaymentIconType =
                    adyenToIconType[method] || "Generic";

                return (
                    <div
                        style={{
                            display: "flex",
                            gap: "20px",
                            alignItems: "center",
                        }}
                    >
                        <PaymentIcon
                            type={iconType}
                            format="flatRounded"
                            width={45}
                        />
                        {iconType}
                    </div>
                );
            },
            minWidth: 150,
        },
        {
            field: "PaymentTerminalId",
            headerName: t("Payment Terminal ID"),
            flex: 1.25,
            headerClassName: customHeader,
            minWidth: 150,
        },
        {
            field: "CardDetail",
            headerName: t("Card Number"),
            flex: 1,
            headerClassName: customHeader,
            minWidth: 150,
        },
        {
            field: "Statut",
            headerName: t("Status"),
            flex: 1,
            headerClassName: customHeader,
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <div
                        style={{
                            display: "flex",
                            gap: "20px",
                            alignItems: "center",
                        }}
                    >
                        <span
                            className={`badge badge-pill ${
                                params.row.Status
                                    ? "badge-success"
                                    : "badge-danger"
                            }`}
                            style={{
                                paddingTop: "8px",
                                paddingBottom: "9px",
                                fontSize: "13px",
                                width: "100px",
                            }}
                        >
                            {params.row.Status ? t("Validated") : t("Refused")}
                        </span>
                    </div>
                );
            },

            minWidth: 150,
        },
        // {
        //     field: "actions",
        //     flex: 1,
        //     headerName: t("Actions"),
        //     headerClassName: "custom-header",
        //     renderCell: (params: GridRenderCellParams) => (
        //         <div
        //             style={{
        //                 display: "flex",
        //                 gap: "20px",
        //                 alignItems: "center",
        //             }}
        //         >
        //             <Tooltip title="Rembourser la transaction" arrow>
        //                 <button
        //                     type="button"
        //                     disabled={!params.row.Status}
        //                     onClick={async () => {
        //                         console.log("111")
        //                         setIsRefundModalOpened(!isRefundModalOpened)
        //                     }}
        //                     aria-label="refund CSV"
        //                     style={{
        //                         display: "flex",
        //                         alignItems: "center",
        //                         gap: "8px",
        //                         backgroundColor: mainColor,
        //                         color: "white",
        //                         border: "none",
        //                         borderRadius: "8px",
        //                         padding: "8px 12px",
        //                         cursor: params.row.Status ? "auto" : "none",
        //                         opacity: params.row.Status ? "1" : "0.5",
        //                     }}
        //                 >
        //                     <img
        //                         src={refund}
        //                         height={18}
        //                         width={18}
        //                         alt="refund CSV"
        //                         style={{ mixBlendMode: "normal" }}
        //                     />
        //                     Rembourser
        //                 </button>
        //             </Tooltip>
        //         </div>
        //     ),
        //     minWidth: 100,
        //     sortable: false,
        // },
    ];

    const handleFilter = () => {
        setFilterAmount(!filterAmount);
    };

    const toggleOpeningModal = React.useCallback(() => {
        setIsModalOpened((prevState) => !prevState);
    }, []);

    const handleToggleOpeningModal = React.useCallback(() => {
        toggleOpeningModal();
    }, [toggleOpeningModal]);

    const toggleOpenedPopover = React.useCallback(() => {
        setIsPopoverOpened((prevState) => !prevState);
    }, []);

    const handleToggleOpenedPopover = React.useCallback(() => {
        toggleOpenedPopover();
    }, [toggleOpenedPopover]);

    function formatDate(dateString: any) {
        const date = new Date(dateString * 1000);
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();

        return `${day}/${month}/${year}`;
    }

    async function getPaiements() {
        const filterPeriod = getAdvancedPeriodStringParam(
            periodSlice,
            advancedDate
        );

        const encodedFilterPeriod = encodeURIComponent(filterPeriod);

        const params = new URLSearchParams({
            inputDate: encodedFilterPeriod,
            uuidEntity: uuidEntity ?? "",
            filter: filter,
            merchentAccount: process.env.REACT_APP_MERCHENT_POS || "",
            minAmount: minAmount !== "" ? String(minAmount) : "0",
            maxAmount: maxAmount !== "" ? String(maxAmount) : "Infinity",
        });

        const response = await fetch(
            `${
                process.env.REACT_APP_ADMIN_ACCESS
            }/getAllTransactions?${params.toString()}`
        );

        if (!response.ok) {
            throw new Error(`Error fetching data: ${response.status}`);
        }
        const data = await response.json();
        return data.map((item: any, index: number) => {
            return {
                id: index,
                date: item.date,
                heure: item.heure,
                PspReference: item.pspReference,
                Amount: item.amount,
                PaymentMethod: item.paymentMethod,
                PaymentTerminalId: item.paymentTerminalId,
                CardDetail: item.cardNumber,
                Status: item.status,
                transactiondata: item.transactiondata,
                uidshop: item.uidshop,
                uidFranchise: item.uidFranchise,
            };
        });
    }

    function FilterButton({ onFilterChange }: any) {
        const [isOpen, setIsOpen] = React.useState(false);

        return (
            <div style={{ position: "relative", display: "inline-block" }}>
                <button
                    className="filter-button"
                    type="button"
                    onClick={() => setIsOpen(!isOpen)}
                    style={{
                        color: mainColor,
                        borderColor: mainColor,
                    }}
                >
                    {textIcon}
                </button>

                {isOpen && (
                    <ul
                        style={{
                            position: "absolute",
                            top: "100%",
                            left: 0,
                            backgroundColor: "white",
                            border: "1px solid #ddd",
                            padding: 0,
                            margin: 0,
                            listStyle: "none",
                            width: "172px",
                            zIndex: 1,
                        }}
                    >
                        <li
                            style={{ padding: "10px", cursor: "pointer" }}
                            onClick={() => {
                                onFilterChange("all");
                                setTextIcon("Tous");
                                setIsOpen(false);
                            }}
                        >
                            Tous les paiements
                        </li>
                        <li
                            style={{ padding: "10px", cursor: "pointer" }}
                            onClick={() => {
                                onFilterChange("validated");
                                setTextIcon("Validés");
                                setIsOpen(false);
                            }}
                        >
                            Validés
                        </li>
                        <li
                            style={{ padding: "10px", cursor: "pointer" }}
                            onClick={() => {
                                onFilterChange("refused");
                                setTextIcon("Refusés");
                                setIsOpen(false);
                            }}
                        >
                            Refusés
                        </li>
                    </ul>
                )}
            </div>
        );
    }

    React.useEffect(() => {
        const loadRows = async () => {
            const fetchedRows: any = await getPaiements();
            setRows(fetchedRows);
        };

        loadRows();
        if (periodSlice.date === "Personnalisée") {
            setSelectedDate(
                `Date : ${formatDate(advancedDate.startDate)} - ${formatDate(
                    advancedDate.endDate
                )}`
            );
        } else {
            setSelectedDate(periodSlice.date);
        }

        // eslint-disable-next-line
    }, [uuidEntity, periodSlice, advancedDate, filter, filterAmount]);
    return (
        <div>
            <PageTitle title="Reporting SoftaveraPay" />
            <div>
                <BreadcrumbOptions
                    currentLink={[
                        {
                            item: t("SOFTAVERAPAY"),
                            link: `${process.env.REACT_APP_PORTAIL_API}`,
                        },
                        {
                            item: t(`${"Reporting"}`),
                            link: `${"Reporting"}`,
                        },
                        {
                            item: t(`${"Payments"}`),
                            link: `${"Paiements"}`,
                        },
                    ]}
                />
                <Container fluid>
                    <HeaderComponent
                        title={t(
                            `The payment report displays the list of all payments, whether approved or declined, with the option to filter the results by the desired date and time.`
                        )}
                    />
                    <CardBody
                        style={{
                            padding: "1px",
                            backgroundColor: "white",
                            marginTop: "10px",
                        }}
                    >
                        <fieldset
                            className="p-3 title-legend-style"
                            style={{ width: "100%", height: "637px" }}
                        >
                            <div className="transaction-overview">
                                <div className="filter-buttons">
                                    <button
                                        style={{
                                            color: mainColor,
                                            borderColor: mainColor,
                                        }}
                                        id="date-range-picker-popover"
                                        type="button"
                                        className="date-filter-button"
                                        onClick={handleToggleOpenedPopover}
                                    >
                                        {selectedDate}
                                    </button>
                                    {isPopoverOpened &&
                                        (isMobile === false ? (
                                            <Popover
                                                placement="top"
                                                isOpen={isPopoverOpened}
                                                target="date-range-picker-popover"
                                                toggle={toggleOpenedPopover}
                                                trigger="legacy"
                                            >
                                                <StyledPopoverBody>
                                                    <DateRangePickerWrapper
                                                        toggleOpenedPopover={
                                                            toggleOpenedPopover
                                                        }
                                                        isAdvancedReporting
                                                    />
                                                </StyledPopoverBody>
                                            </Popover>
                                        ) : (
                                            <Modal
                                                isOpen={isModalOpened}
                                                toggle={
                                                    handleToggleOpeningModal
                                                }
                                                className="defined-range-modal__clz"
                                                centered
                                                fade={false}
                                            >
                                                <MobileDateRangePickerWrapper
                                                    toggleOpeningModal={
                                                        handleToggleOpeningModal
                                                    }
                                                />
                                            </Modal>
                                        ))}

                                    <FilterButton
                                        onFilterChange={(filter: string) =>
                                            setFilter(filter)
                                        }
                                    />

                                    <AmountPopoverFilter
                                        onApply={handleFilter}
                                        minAmount={minAmount}
                                        maxAmount={maxAmount}
                                        setMinAmount={setMinAmount}
                                        setMaxAmount={setMaxAmount}
                                        setBtnText={setBtnText}
                                        btnText={btnText}
                                    />

                                    <button
                                        style={{
                                            color: mainColor,
                                            borderColor: mainColor,
                                        }}
                                        className="remove-all-filter-button"
                                        type="button"
                                        onClick={async () => {
                                            setFilter("all");
                                            setMinAmount(0),
                                                setMaxAmount("Infinity");
                                            setTextIcon(t("Status"));
                                            setBtnText(t("Amount"));
                                            dispatch(changePeriod(t("Today")));
                                            setFilterAmount(!filterAmount);
                                        }}
                                    >
                                        {t("Remove all filters")}
                                    </button>

                                    <button
                                        style={{
                                            marginLeft: !showDetails
                                                ? "52%"
                                                : "54%",
                                            width: "144px",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "8px",
                                            backgroundColor: mainColor,
                                            color: "white",
                                            border: "none",
                                            borderRadius: "8px",
                                            padding: "8px 12px",
                                        }}
                                        type="button"
                                        onClick={async () => {
                                            const fetchedRows: any =
                                                await getPaiements();
                                            setRows(fetchedRows);
                                            setFilter("all");
                                            setTextIcon("Tous");
                                        }}
                                        aria-label="refresh CSV"
                                    >
                                        <img
                                            src={refresh}
                                            height={18}
                                            width={18}
                                            alt="refresh CSV"
                                            style={{
                                                mixBlendMode: "normal",
                                            }}
                                        />
                                        {t("Refresh")}
                                    </button>
                                </div>
                            </div>

                            <div
                                style={{
                                    display: "flex",
                                    height: "80%",
                                    width: "100%",
                                }}
                            >
                                <DataGrid
                                    rows={rows.slice(
                                        currentPage * pageSize,
                                        (currentPage + 1) * pageSize
                                    )}
                                    columns={columns}
                                    localeText={localeText}
                                    components={{
                                        NoRowsOverlay: NoDataOverlay,
                                        Footer: CustomFooter,
                                    }}
                                    componentsProps={{
                                        footer: {
                                            currentPage,
                                            onPageChange: handlePageChange,
                                            totalPages,
                                            pageSize,
                                            onPageSizeChange:
                                                handlePageSizeChange,
                                        },
                                    }}
                                    disableSelectionOnClick
                                />

                                {/* {!showDetails && (
                                    <div
                                        style={{
                                            width: "34%",
                                            marginLeft: "10px",
                                            height: "525px",
                                        }}
                                    >
                                        <TransactionDetails
                                            selectedRow={selectedRow}
                                            onRefund={() =>
                                                console.log("Refund triggered")
                                            }
                                            onClose={() =>
                                                setShowDetails(!showDetails)
                                            }
                                        />
                                    </div>
                                )} */}
                            </div>

                            {/* <div style={{ height: "473px", width: "100%" }}>
                                <DataGrid
                                    rows={rows.slice(
                                        currentPage * pageSize,
                                        (currentPage + 1) * pageSize
                                    )}
                                    columns={columns}
                                    localeText={localeText}
                                    components={{
                                        NoRowsOverlay: NoDataOverlay,
                                        Footer: CustomFooter,
                                    }}
                                    componentsProps={{
                                        footer: {
                                            currentPage,
                                            onPageChange: handlePageChange,
                                            totalPages,
                                            pageSize,
                                            onPageSizeChange:
                                                handlePageSizeChange,
                                        },
                                    }}
                                    disableSelectionOnClick
                                />
                            </div> */}
                        </fieldset>
                    </CardBody>
                </Container>
            </div>
            {/* <RefundModal
                isModalOpened={isRefundModalOpened}
                setIsModalOpened={setIsRefundModalOpened}
            /> */}
        </div>
    );
}
